import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

import { API } from "../App";
import { currentRegion } from "../config/regions";
import { useLanguage } from "../i18n/LanguageContext";
import {
  UNIT_SYSTEM_STORAGE_KEY,
  resolveUnitSystem,
  useMeasurementDraft,
} from "../lib/bodyUnits";
import {
  trackHealthSectionSaved,
  trackHealthSectionShown,
} from "../lib/analytics";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  ACTIVITY_OPTIONS,
  ALCOHOL_OPTIONS,
  SECTION_FIELDS,
  SMOKING_OPTIONS,
  buildSectionSave,
  healthSectionSaveStatus,
  sectionChoicesComplete,
  sectionFormState,
} from "../lib/healthSections";

export function extendedProfileUrl(subjectId) {
  return subjectId
    ? `${API}/subjects/${subjectId}/profile/extended`
    : `${API}/profile/health/extended`;
}

export function healthSaveErrorMessage(err, t) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((item) => item?.msg || item).join("; ");
  }
  return t("healthProfile.saveError");
}

export async function putHealthExtended(subjectId, body) {
  const res = await axios.put(extendedProfileUrl(subjectId), body);
  return res.data?.data;
}

function useUnitSystem() {
  const [system, setSystem] = useState(() =>
    resolveUnitSystem(
      currentRegion,
      typeof localStorage === "undefined"
        ? null
        : localStorage.getItem(UNIT_SYSTEM_STORAGE_KEY),
    ),
  );
  const choose = (next) => {
    setSystem(next);
    try {
      localStorage.setItem(UNIT_SYSTEM_STORAGE_KEY, next);
    } catch {
      /* preference is optional */
    }
  };
  return [system, choose];
}

function UnitSwitcher({ system, onChange, t }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <span className="text-sm text-muted-foreground">{t("healthProfile.units.label")}</span>
      <div className="flex gap-2" role="group" aria-label={t("healthProfile.units.label")}>
        <Button
          type="button"
          size="sm"
          variant={system === "metric" ? "default" : "outline"}
          aria-pressed={system === "metric"}
          data-testid="health-units-metric"
          onClick={() => onChange("metric")}
        >
          {t("healthProfile.units.metric")}
        </Button>
        <Button
          type="button"
          size="sm"
          variant={system === "imperial" ? "default" : "outline"}
          aria-pressed={system === "imperial"}
          data-testid="health-units-imperial"
          onClick={() => onChange("imperial")}
        >
          {t("healthProfile.units.imperial")}
        </Button>
      </div>
    </div>
  );
}

function MeasurementFields({ heightCm, weightKg, onChange }) {
  const { t } = useLanguage();
  const [system, setSystem] = useUnitSystem();
  const draftApi = useMeasurementDraft(system, heightCm, weightKg, onChange);
  const { draft } = draftApi;

  return (
    <div className="space-y-3">
      <UnitSwitcher system={system} onChange={setSystem} t={t} />
      {system === "imperial" ? (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="space-y-1.5">
            <Label>{t("healthProfile.heightFt")}</Label>
            <Input
              inputMode="decimal"
              value={draft.feet}
              onChange={(e) => draftApi.setFeet(e.target.value)}
              data-testid="health-height-ft"
            />
          </div>
          <div className="space-y-1.5">
            <Label>{t("healthProfile.heightIn")}</Label>
            <Input
              inputMode="decimal"
              value={draft.inches}
              onChange={(e) => draftApi.setInches(e.target.value)}
              data-testid="health-height-in"
            />
          </div>
          <div className="space-y-1.5">
            <Label>{t("healthProfile.weightLb")}</Label>
            <Input
              inputMode="decimal"
              value={draft.weight}
              onChange={(e) => draftApi.setWeight(e.target.value)}
              data-testid="health-weight-lb"
            />
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>{t("healthProfile.heightCm")}</Label>
            <Input
              inputMode="decimal"
              value={draft.height}
              onChange={(e) => draftApi.setHeight(e.target.value)}
              data-testid="health-height"
            />
          </div>
          <div className="space-y-1.5">
            <Label>{t("healthProfile.weightKg")}</Label>
            <Input
              inputMode="decimal"
              value={draft.weight}
              onChange={(e) => draftApi.setWeight(e.target.value)}
              data-testid="health-weight"
            />
          </div>
        </div>
      )}
    </div>
  );
}

function ChoiceButtons({ field, value, onChange, t }) {
  return (
    <div className="flex gap-2" role="group" aria-label={t(`healthProfile.question.${field}`)}>
      <Button
        type="button"
        size="sm"
        variant={value === "yes" ? "default" : "outline"}
        aria-pressed={value === "yes"}
        data-testid={`health-${field}-yes`}
        onClick={() => onChange({ [field]: "yes" })}
      >
        {t("healthProfile.yes")}
      </Button>
      <Button
        type="button"
        size="sm"
        variant={value === "no" ? "default" : "outline"}
        aria-pressed={value === "no"}
        data-testid={`health-${field}-no`}
        onClick={() => onChange({ [field]: "no" })}
      >
        {t("healthProfile.no")}
      </Button>
    </div>
  );
}

function EnumSelect({ label, value, options, optionPrefix, placeholder, testId, onChange }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Select value={value || undefined} onValueChange={onChange}>
        <SelectTrigger data-testid={testId}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((opt) => (
            <SelectItem key={opt} value={opt}>
              {optionPrefix(opt)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

/**
 * Fields for a single progressive section. Legacy-only fields stay on the full form.
 */
export function HealthSectionFields({ section, state, onChange }) {
  const { t } = useLanguage();

  if (section === "lifestyle") {
    return (
      <div className="space-y-3">
        <MeasurementFields
          heightCm={state.height_cm}
          weightKg={state.weight_kg}
          onChange={onChange}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <EnumSelect
            label={t("healthProfile.smoking")}
            value={state.smoking}
            options={SMOKING_OPTIONS}
            optionPrefix={(opt) => t(`healthProfile.smoking.${opt}`)}
            placeholder={t("healthProfile.selectPlaceholder")}
            testId="health-smoking"
            onChange={(value) => onChange({ smoking: value })}
          />
          <EnumSelect
            label={t("healthProfile.alcohol")}
            value={state.alcohol}
            options={ALCOHOL_OPTIONS}
            optionPrefix={(opt) => t(`healthProfile.alcohol.${opt}`)}
            placeholder={t("healthProfile.selectPlaceholder")}
            testId="health-alcohol"
            onChange={(value) => onChange({ alcohol: value })}
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <EnumSelect
            label={t("healthProfile.activityLevel")}
            value={state.activity_level}
            options={ACTIVITY_OPTIONS}
            optionPrefix={(opt) => t(`healthProfile.activity.${opt}`)}
            placeholder={t("healthProfile.selectPlaceholder")}
            testId="health-activity"
            onChange={(value) => onChange({ activity_level: value })}
          />
          <div className="space-y-1.5">
            <Label>{t("healthProfile.sleepHours")}</Label>
            <Input
              type="number"
              min={0}
              max={24}
              step="0.5"
              value={state.sleep_hours_typical}
              onChange={(e) => onChange({ sleep_hours_typical: e.target.value })}
              data-testid="health-sleep-hours"
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {SECTION_FIELDS[section].map((field) => {
        const textKey = field === "surgeries_recent" ? "surgeriesText" : `${field}Text`;
        const showText = state[field] === "yes";
        return (
          <div key={field} className="space-y-1.5">
            <Label>{t(`healthProfile.question.${field}`)}</Label>
            <ChoiceButtons field={field} value={state[field]} onChange={onChange} t={t} />
            {showText && field === "surgeries_recent" && (
              <Textarea
                value={state.surgeriesText}
                onChange={(e) => onChange({ surgeriesText: e.target.value })}
                className="min-h-[60px]"
                data-testid="health-surgeries_recent-input"
              />
            )}
            {showText && field !== "surgeries_recent" && (
              <Input
                value={state[textKey]}
                onChange={(e) => onChange({ [textKey]: e.target.value })}
                placeholder={t("healthProfile.listPlaceholder")}
                data-testid={`health-${field}-input`}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

const HealthSectionForm = ({
  section,
  subjectId = null,
  initialExtended = {},
  onSaved,
  submitLabel,
  analytics = null,
}) => {
  const { t } = useLanguage();
  const [state, setState] = useState(() => sectionFormState(initialExtended));
  const [saving, setSaving] = useState(false);
  const extendedKey = JSON.stringify(initialExtended ?? {});
  const analyticsSurface = analytics?.surface;
  const analyticsKind = analytics?.subjectKind;
  const trackShown = Boolean(analytics) && analytics.trackShown !== false;

  useEffect(() => {
    setState(sectionFormState(initialExtended));
    // extendedKey tracks content; the object identity may change every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extendedKey]);

  useEffect(() => {
    if (!trackShown) return;
    trackHealthSectionShown({
      section,
      surface: analyticsSurface,
      subjectKind: analyticsKind,
    });
  }, [trackShown, section, analyticsSurface, analyticsKind]);

  const patch = (partial) => setState((prev) => ({ ...prev, ...partial }));
  const ready = sectionChoicesComplete(section, state);

  const handleSave = async () => {
    if (!ready || saving) return;
    setSaving(true);
    try {
      const body = buildSectionSave(section, state);
      const data = await putHealthExtended(subjectId, body);
      if (analytics) {
        trackHealthSectionSaved({
          section,
          surface: analyticsSurface,
          subjectKind: analyticsKind,
          status: healthSectionSaveStatus(body),
        });
      }
      toast.success(t("healthProfile.extendedSaved"));
      onSaved?.(data);
    } catch (err) {
      toast.error(healthSaveErrorMessage(err, t));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3" data-testid={`health-section-form-${section}`}>
      <HealthSectionFields section={section} state={state} onChange={patch} />
      <Button
        type="button"
        onClick={handleSave}
        disabled={saving || !ready}
        data-testid="health-section-save"
      >
        {saving ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          submitLabel || t("healthProfile.saveSection")
        )}
      </Button>
    </div>
  );
};

export default HealthSectionForm;
